import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitSTIA, getSTIA } from '../../service/stiaAssessment';
import {
  Box, Button, Card, CardContent, Typography, Radio, RadioGroup,
  FormControlLabel, FormControl, Container, LinearProgress,
  TextField, useTheme, useMediaQuery, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import echobuddyLogo from '../../assets/newEchoLogo.png';
import { useLocation } from 'react-router-dom';
const SCALE_4 = ['Almost Never', 'Sometimes', 'Often', 'Almost Always'];

const sections = [
  {
    id: 'a',
    label: 'Section A — Stress & Relaxation',
    color: '#38BDF8',
    bg: 'rgba(56,189,248,0.08)',
    questions: [
      { id: 'a1', text: 'echoShine-STIAsessions help me feel calmer.' },
      { id: 'a2', text: 'I feel less anxious after listening to my echoShine sound tracks.' },
      { id: 'a3', text: 'I am able to relax more easily since starting echoShine.' },
      { id: 'a4', text: 'I notice a reduction in physical tension (shoulders, jaw, stomach) after sessions.' },
    ]
  },
  {
    id: 'b',
    label: 'Section B — Sleep Quality & Rest',
    color: '#818CF8',
    bg: 'rgba(129,140,248,0.08)',
    questions: [
      { id: 'b1', text: 'I fall asleep more easily on days I use echoShine.' },
      { id: 'b2', text: 'My overall sleep quality has improved since starting echoShine.' },
      { id: 'b3', text: 'I wake up feeling more rested.' },
      { id: 'b4', text: 'I experience fewer disturbed nights.' },
    ]
  },
  {
    id: 'c',
    label: 'Section C — Focus & Concentration',
    color: '#2CC795',
    bg: 'rgba(44,199,149,0.08)',
    questions: [
      { id: 'c1', text: 'I find it easier to concentrate on my studies after an echoShine session.' },
      { id: 'c2', text: 'My mind feels less cluttered during lectures/study time.' },
      { id: 'c3', text: 'I am able to sustain attention for longer periods.' },
      { id: 'c4', text: 'I feel mentally sharper during the day.' },
    ]
  },
  {
    id: 'd',
    label: 'Section D — Emotional Regulation & Mood',
    color: '#F9BE4A',
    bg: 'rgba(249,190,74,0.08)',
    questions: [
      { id: 'd1', text: 'I feel more in control of my emotions.' },
      { id: 'd2', text: 'My mood feels more stable through the day.' },
      { id: 'd3', text: 'I recover more quickly from stressful moments.' },
      { id: 'd4', text: 'I feel more positive about my day-to-day college life.' },
    ]
  },
];

const allScoredQuestions = sections.flatMap(s => s.questions);

// echoShine-echo Resonance Scale — Scoring Guide
// Tier thresholds are evaluated on Overall Index (%), highest first.
const ECHO_TIER_SCALE = [
  {
    label: 'Resounding',
    min: 85,
    range: '85–100%',
    color: '#22c55e',
    signals: 'Sound Therapy is clearly landing',
    reading: 'Strong, consistent benefit across stress, sleep, focus and mood.',
    action: 'Reinforce and sustain; candidate for peer-mentor/ambassador roles and testimonial (with consent).',
  },
  {
    label: 'Harmonized',
    min: 70,
    range: '70–84.9%',
    color: '#14b8a6',
    signals: 'Solid, dependable benefit',
    reading: 'Most domains responding well; occasional off days.',
    action: 'Light-touch check-ins; no action needed beyond routine monthly tracking.',
  },
  {
    label: 'Attuning',
    min: 50,
    range: '50–69.9%',
    color: '#8b5cf6',
    signals: 'Benefit is emerging but uneven',
    reading: 'Some domains improving, others flat — a normal mid-program state.',
    action: 'Nudge toward more consistent session use; revisit at next Pre/Post cycle.',
  },
  {
    label: 'Muffled',
    min: 30,
    range: '30–49.9%',
    color: '#f59e0b',
    signals: 'Limited reported benefit',
    reading: 'Self-report is low but this can reflect low usage as much as low benefit.',
    action: 'Cross-check against Monthly Usage Report first — low completion vs. genuine non-response need different fixes.',
  },
  {
    label: 'Silent',
    min: 0,
    range: '0–29.9%',
    color: '#ef4444',
    signals: 'No meaningful benefit reported',
    reading: 'Low across nearly all domains, independent of usage.',
    action: 'Needs Attention.',
  },
];

const getEchoTier = (pct) =>
  ECHO_TIER_SCALE.find(t => pct >= t.min) || ECHO_TIER_SCALE[ECHO_TIER_SCALE.length - 1];

const StyledCard = styled(Card)(() => ({
  borderRadius: 20,
  boxShadow: '0 12px 40px rgba(16,24,40,0.08)',
  border: '1px solid #E5E7EB',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
}));

const OptionButton = styled(FormControlLabel)(({ checked }) => ({
  margin: '6px 0',
  padding: '12px 16px',
  borderRadius: 14,
  border: checked ? '2px solid #4a6cf7' : '1px solid #E5E7EB',
  backgroundColor: checked ? 'rgba(74,108,247,0.06)' : '#ffffff',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': { borderColor: '#4a6cf7', backgroundColor: 'rgba(74,108,247,0.04)' },
  '& .MuiFormControlLabel-label': { width: '100%', marginLeft: 14 },
}));

const STIASurvey = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showWelcome, setShowWelcome] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [engagement, setEngagement] = useState({ e1: '', e2: '', e3: '' });
  const [showEngagement, setShowEngagement] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user?.id;

  useEffect(() => {
    if (location.state?.newAssessment) return;

    const fetchPrev = async () => {
      if (!userId) return;
      try {
        const data = await getSTIA(userId);
        if (data?.answers) {
          const parsed = typeof data.answers === 'string' ? JSON.parse(data.answers) : data.answers;
          // Don't pre-fill the question form with previous answers —
          // just keep the computed score available for "View Score".
          setResult(calculateScores(parsed));
          setHasPrevious(true);
        }
      } catch {
        // no previous assessment
      }
    };
    fetchPrev();
  }, [userId]);

  const totalQ = allScoredQuestions.length;
  const progress = showEngagement
    ? 100
    : Math.round(((currentQuestion) / totalQ) * 100);

  const currentQ = allScoredQuestions[currentQuestion];
  const currentSection = sections.find(s => s.questions.some(q => q.id === currentQ?.id));

  const handleOptionSelect = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: Number(value) }));
  };

  const handleNext = () => {
    if (currentQuestion < totalQ - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowEngagement(true);
    }
  };

  const handlePrevious = () => {
    if (showEngagement) {
      setShowEngagement(false);
      setCurrentQuestion(totalQ - 1);
    } else if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const calculateScores = (src = answers) => {
    const sum = (ids) => ids.reduce((t, id) => t + (src[id] || 0), 0);
    const scoreA = sum(['a1', 'a2', 'a3', 'a4']);
    const scoreB = sum(['b1', 'b2', 'b3', 'b4']);
    const scoreC = sum(['c1', 'c2', 'c3', 'c4']);
    const scoreD = sum(['d1', 'd2', 'd3', 'd4']);
    const rawTotal = scoreA + scoreB + scoreC + scoreD;
    const overallPct = Math.round((rawTotal / 64) * 100);
    return { scoreA, scoreB, scoreC, scoreD, rawTotal, overallPct };
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== totalQ) {
      alert('Please answer all scored questions before submitting.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        userId,
        answers: { ...answers },
        engagement: { e1: engagement.e1 || '', e2: engagement.e2 || '', e3: engagement.e3 || '' },
      };
      const response = await submitSTIA(payload);
      setResult(response);

      // Reset all radio-button answers
      setAnswers({});

      // Reset engagement fields
      setEngagement({
        e1: '',
        e2: '',
        e3: '',
      });

      // Reset question position
      setCurrentQuestion(0);

      // Reset section/form state
      setShowEngagement(false);

      setIsSubmitted(true);
    } catch (err) {
      console.error('STIA submission failed:', err);
      alert('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.newAssessment) {
      setAnswers({});
      setEngagement({
        e1: '',
        e2: '',
        e3: '',
      });

      setCurrentQuestion(0);
      setShowEngagement(false);
      setIsSubmitted(false);
      setResult(null);
      setHasPrevious(false);

      // Remove the navigation state so refresh doesn't repeatedly reset
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const resetSTIAForm = () => {
    setAnswers({});
    setEngagement({
      e1: '',
      e2: '',
      e3: '',
    });

    setCurrentQuestion(0);
    setShowEngagement(false);
    setIsSubmitted(false);
    setResult(null);
    setHasPrevious(false);
  };

  // ── HEADER ──────────────────────────────────────────────────────────────
  const Header = () => (
    <Box sx={{
      width: '100%', px: { xs: 2, sm: 4, md: 6 }, py: 1,
      display: 'flex', alignItems: 'center',
      backgroundColor: 'white', borderBottom: '1px solid #E5E7EB',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src={echobuddyLogo} alt="echoShine" style={{ height: 45, width: 45, borderRadius: '50%', objectFit: 'cover' }} />
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#7843f3', lineHeight: 1.1, mb: 0.2 }}>
              echoShine
            </Typography>
            <Typography variant="caption" sx={{ color: '#7843f3', fontSize: '0.65rem', fontWeight: 500, display: 'block' }}>
              Connect. Heal. Rise.
            </Typography>
          </Box>
        </Link>
      </Box>
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
        <Chip label="STIA" size="small" icon={<HeadphonesIcon sx={{ fontSize: 14 }} />}
          sx={{ background: 'linear-gradient(135deg,#22c55e,#14b8a6)', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
        <Button startIcon={<ArrowBackIosIcon />} onClick={() => navigate('/home')}
          sx={{ textTransform: 'none', color: '#6b7c93', fontSize: '0.85rem' }}>
          Home
        </Button>
      </Box>
    </Box>
  );

  // ── WELCOME ──────────────────────────────────────────────────────────────
  if (showWelcome) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
        <Header />
        <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
          <StyledCard>
            <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 3,
                background: 'linear-gradient(135deg,#22c55e,#14b8a6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <HeadphonesIcon sx={{ color: '#fff', fontSize: 36 }} />
              </Box>
              <Typography variant="h5" fontWeight={700} mb={1} sx={{
                background: 'linear-gradient(135deg,#22c55e,#14b8a6,#8b5cf6)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>
                Sound Therapy Impact Assessment
              </Typography>
              <Chip label="STIA" size="small" sx={{ mb: 2, background: 'linear-gradient(135deg,#22c55e,#14b8a6)', color: '#fff', fontWeight: 700 }} />
              <Typography color="text.secondary" sx={{ mb: 1, fontSize: '0.9rem', lineHeight: 1.6 }}>
                This assessment measures how echoShine sound therapy is impacting your stress, sleep, focus, and emotional wellbeing.
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3, fontSize: '0.85rem' }}>
                <strong>16 scored questions</strong> across 4 sections + 3 short feedback questions. Takes ~5 minutes.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, textAlign: 'left' }}>
                {sections.map(s => (
                  <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.82rem', color: '#4b5563' }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Button fullWidth variant="contained" size="large"
                onClick={() => setShowWelcome(false)}
                sx={{
                  py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '1rem',
                  background: 'linear-gradient(135deg,#22c55e,#14b8a6,#8b5cf6)',
                  boxShadow: '0 8px 20px rgba(34,197,94,0.3)',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(34,197,94,0.4)' },
                  transition: 'all 0.3s ease'
                }}>
                Begin Assessment
              </Button>
              {hasPrevious && (
                <Button fullWidth variant="text" size="small" onClick={() => setIsSubmitted(true)}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600, color: '#14b8a6' }}>
                  View Score
                </Button>
              )}
            </CardContent>
          </StyledCard>
        </Container>
      </Box>
    );
  }

  // ── RESULTS — OVERALL echoShine-STIA IMPACT SUMMARY ─────────────────────
  if (isSubmitted) {
    const localScores = calculateScores();
    const pct = result?.overallPct ?? localScores.overallPct;
    const sA = result?.scoreA ?? localScores.scoreA;
    const sB = result?.scoreB ?? localScores.scoreB;
    const sC = result?.scoreC ?? localScores.scoreC;
    const sD = result?.scoreD ?? localScores.scoreD;
    const raw = result?.rawTotal ?? localScores.rawTotal;

    // Echo Tier is derived from the Overall Index (%) using the
    // echoShine-echo Resonance Scale scoring guide.
    const tier = getEchoTier(pct);

    const summaryRows = [
      { label: 'Section A: Stress & Relaxation', value: `${sA} out of 16` },
      { label: 'Section B: Sleep Quality & Rest', value: `${sB} out of 16` },
      { label: 'Section C: Focus & Concentration', value: `${sC} out of 16` },
      { label: 'Section D: Emotional Regulation & Mood', value: `${sD} out of 16` },
    ];

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
        <Header />
        <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
          <StyledCard>
            <CardContent sx={{ p: 0 }}>
              {/* Summary header bar */}
              <Box sx={{ bgcolor: '#4fb8ac', color: '#fff', px: { xs: 2.5, sm: 3 }, py: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: 0.3 }}>
                  OVERALL echoShine-STIA IMPACT SUMMARY
                </Typography>
              </Box>

              {/* Section score rows */}
              <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
                {summaryRows.map(row => (
                  <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.9, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#334155' }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#334155' }}>{row.value}</Typography>
                  </Box>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, bgcolor: '#ECFDF5', px: 1, mt: 1, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    Raw Total Score
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    {raw} out of 64
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, bgcolor: '#ECFDF5', px: 1, mt: 0.5, borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    Overall Index (%)
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    {pct}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.4, mt: 1 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>
                    Echo Tier
                  </Typography>
                  <Chip label={tier.label} sx={{ bgcolor: tier.color, color: '#fff', fontWeight: 700 }} />
                </Box>
              </Box>

              {/* What It Signals / Reading It / Recommended Action */}
              <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.6 }}>
                  <Typography component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>What It Signals: </Typography>
                  {tier.signals}
                </Typography>
                <Typography sx={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.6 }}>
                  <Typography component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>Reading It: </Typography>
                  {tier.reading}
                </Typography>
                <Typography sx={{ fontSize: '0.86rem', color: '#374151', lineHeight: 1.6 }}>
                  <Typography component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>Recommended Action: </Typography>
                  {tier.action}
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #E5E7EB', pt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/home')} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 500 }}>
                  Back to Home
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Container>
      </Box>
    );
  }

  // ── ENGAGEMENT SECTION (E) ────────────────────────────────────────────────
  if (showEngagement) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
        <Header />
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6b7c93' }}>Section E — Engagement Feedback</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#6b7c93' }}>Optional</Typography>
            </Box>
            <LinearProgress variant="determinate" value={100} sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(135deg,#22c55e,#14b8a6)', borderRadius: 3 } }} />
          </Box>
          <StyledCard>
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h6" fontWeight={700} mb={3} sx={{
                background: 'linear-gradient(135deg,#22c55e,#14b8a6)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
              }}>
                Almost done! Share your experience
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 1, color: '#374151' }}>
                    Q17. How many echoShine sessions did you complete this month?
                  </Typography>
                  <TextField
                    fullWidth
                    type="text"
                    placeholder="Enter number"
                    value={engagement.e1}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow only digits
                      if (/^\d*$/.test(value)) {
                        setEngagement((prev) => ({
                          ...prev,
                          e1: value,
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent +, -, decimal point, e/E and other unwanted characters
                      if (['-', '+', '.', ',', 'e', 'E'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 1, color: '#374151' }}>
                    Q18. What do you enjoy most about the echoShine sessions?
                  </Typography>
                  <TextField fullWidth multiline rows={3} placeholder="Share your thoughts..."
                    value={engagement.e2} onChange={e => setEngagement(prev => ({ ...prev, e2: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 1, color: '#374151' }}>
                    Q19. Is there anything that would make echoShine more useful for you?
                  </Typography>
                  <TextField fullWidth multiline rows={3} placeholder="Your suggestions help us improve..."
                    value={engagement.e3} onChange={e => setEngagement(prev => ({ ...prev, e3: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={handlePrevious} startIcon={<ArrowBackIosIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, flex: 1 }}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading}
                  sx={{ flex: 2, textTransform: 'none', borderRadius: 2, fontWeight: 600, background: 'linear-gradient(135deg,#22c55e,#14b8a6,#8b5cf6)', '&:hover': { transform: 'translateY(-1px)' } }}>
                  {loading ? 'Submitting…' : 'Submit Assessment'}
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Container>
      </Box>
    );
  }

  // ── QUESTION SCREEN ──────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9ff' }}>
      <Header />
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#6b7c93' }}>{currentSection?.label}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#6b7c93' }}>Q{currentQuestion + 1} of {totalQ}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress}
            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { bgcolor: currentSection?.color || '#22c55e', borderRadius: 3 } }} />
        </Box>

        <StyledCard>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: currentSection?.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: currentSection?.color, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {currentSection?.label}
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight={600} mb={3} sx={{ color: '#1e293b', lineHeight: 1.5, fontSize: isMobile ? '1rem' : '1.1rem' }}>
              {currentQ?.text}
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={answers[currentQ?.id] !== undefined ? String(answers[currentQ?.id]) : ''}
                onChange={e => handleOptionSelect(currentQ.id, e.target.value)}>
                {SCALE_4.map((label, idx) => {
                  const val = String(idx + 1);
                  const isChecked = answers[currentQ?.id] === idx + 1;
                  return (
                    <OptionButton key={val} checked={isChecked}
                      value={val} control={<Radio size="small" sx={{ color: currentSection?.color, '&.Mui-checked': { color: currentSection?.color } }} />}
                      label={<Typography sx={{ fontSize: '0.9rem', fontWeight: isChecked ? 600 : 400, color: isChecked ? currentSection?.color : '#374151' }}>
                        {label}
                      </Typography>}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {currentQuestion > 0 && (
                <Button variant="outlined" onClick={handlePrevious} startIcon={<ArrowBackIosIcon />}
                  sx={{ textTransform: 'none', borderRadius: 2, flex: 1 }}>
                  Back
                </Button>
              )}
              <Button variant="contained" onClick={handleNext} disabled={answers[currentQ?.id] === undefined}
                sx={{
                  flex: 2, textTransform: 'none', borderRadius: 2, fontWeight: 600,
                  background: `linear-gradient(135deg, ${currentSection?.color}, #8b5cf6)`,
                  '&:hover': { transform: 'translateY(-1px)' }, transition: 'all 0.2s ease'
                }}>
                {currentQuestion === totalQ - 1 ? 'Next: Feedback' : 'Next'}
              </Button>
            </Box>
          </CardContent>
        </StyledCard>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 0.5, flexWrap: 'wrap' }}>
          {allScoredQuestions.map((q, idx) => {
            const answered = answers[q.id] !== undefined;
            const active = idx === currentQuestion;
            const sec = sections.find(s => s.questions.some(sq => sq.id === q.id));
            return (
              <Box key={q.id} sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: active ? sec?.color : answered ? sec?.color + '99' : '#E5E7EB',
                border: active ? `2px solid ${sec?.color}` : 'none',
                transition: 'all 0.2s'
              }} />
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default STIASurvey;
